import React, { useEffect, useState, memo } from 'react';
import { Item, View, Text } from '@adobe/react-spectrum';
import { Picker } from '@react-spectrum/picker';
import { FormField } from '../../store/slice';
import { fetchDropdownOptions } from '../../store/api';
import styles from '../FormField.module.scss';

interface OptionItem {
  value: string;
  label: string;
}

interface MultiSelectComponentProps {
  field: FormField;
  value: string[];
  onChange: (name: string, value: string[]) => void;
  formValues: Record<string, any>;
  onFocus?: () => void;
  disabled?: boolean;
}

/**
 * Component for rendering multi-select dropdown fields
 */
export const MultiSelectComponent: React.FC<MultiSelectComponentProps> = memo(
  ({ field, value = [], onChange, formValues, onFocus, disabled = false }) => {
    const [options, setOptions] = useState<OptionItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch options from API if needed
    useEffect(() => {
      const fetchOptionsFromApi = async () => {
        // Only fetch if this has an API endpoint
        if (!field.api) {
          return;
        }

        setIsLoading(true);
        setError(null);

        try {
          // Check dependency
          if (field.dependsOn && !formValues[field.dependsOn]) {
            setOptions([]);
            setIsLoading(false);
            return;
          }

          const options = await fetchDropdownOptions(
            field.api,
            field.dependsOn
              ? { [field.dependsOn]: formValues[field.dependsOn] }
              : undefined
          );

          setOptions(
            options.map(item => ({
              value: item.id || item.value || '',
              label: item.name || item.label || ''
            }))
          );
        } catch (error) {
          setError(`Failed to load options`);
        } finally {
          setIsLoading(false);
        }
      };

      fetchOptionsFromApi();
    }, [
      field.name,
      field.api,
      field.dependsOn,
      field.dependsOn ? formValues?.[field.dependsOn] : undefined
    ]);

    // If we have predefined options, use those instead of API
    useEffect(() => {
      if (field.options) {
        setOptions(field.options);
      }
    }, [field.options]);

    return (
      <View
        UNSAFE_className={styles.formField}
        data-testid={`multi-select-container-${field.name
          .replace(/[^a-zA-Z0-9]/g, '-')
          .toLowerCase()}`}
      >
        <label
          className={styles.label}
          data-testid={`multi-select-label-${field.name
            .replace(/[^a-zA-Z0-9]/g, '-')
            .toLowerCase()}`}
        >
          {field.label}
          {field.required && (
            <span
              className={styles.required}
              data-testid={`multi-select-required-${field.name
                .replace(/[^a-zA-Z0-9]/g, '-')
                .toLowerCase()}`}
            >
              *
            </span>
          )}
        </label>

        {/* Using React Spectrum's multi-select implementation */}
        <div
          className={styles.multiSelectContainer}
          data-testid={`multi-select-options-container-${field.name
            .replace(/[^a-zA-Z0-9]/g, '-')
            .toLowerCase()}`}
          onFocus={onFocus}
        >
          {options.map(option => (
            <div
              key={option.value}
              className={styles.multiSelectItem}
              data-testid={`multi-select-item-${field.name
                .replace(/[^a-zA-Z0-9]/g, '-')
                .toLowerCase()}-${option.value
                .replace(/[^a-zA-Z0-9]/g, '-')
                .toLowerCase()}`}
            >
              <input
                type="checkbox"
                id={`${field.name}-${option.value}`}
                checked={value.includes(option.value)}
                onChange={e => {
                  const newValue = [...value];
                  if (e.target.checked) {
                    if (!newValue.includes(option.value)) {
                      newValue.push(option.value);
                    }
                  } else {
                    const index = newValue.indexOf(option.value);
                    if (index !== -1) {
                      newValue.splice(index, 1);
                    }
                  }
                  onChange(field.name, newValue);
                }}
                disabled={field.disabled || isLoading || disabled}
                data-testid={`multi-select-checkbox-${field.name
                  .replace(/[^a-zA-Z0-9]/g, '-')
                  .toLowerCase()}-${option.value
                  .replace(/[^a-zA-Z0-9]/g, '-')
                  .toLowerCase()}`}
              />
              <label
                htmlFor={`${field.name}-${option.value}`}
                data-testid={`multi-select-option-label-${field.name
                  .replace(/[^a-zA-Z0-9]/g, '-')
                  .toLowerCase()}-${option.value
                  .replace(/[^a-zA-Z0-9]/g, '-')
                  .toLowerCase()}`}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>

        {isLoading && (
          <Text
            data-testid={`multi-select-loading-${field.name
              .replace(/[^a-zA-Z0-9]/g, '-')
              .toLowerCase()}`}
          >
            Loading options...
          </Text>
        )}
        {error && (
          <Text
            UNSAFE_className={styles.errorText}
            data-testid={`multi-select-error-${field.name
              .replace(/[^a-zA-Z0-9]/g, '-')
              .toLowerCase()}`}
          >
            {error}
          </Text>
        )}
      </View>
    );
  }
);
